import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { SystemTheme } from '@core/models/base.model';
import { Settings, UserLoginResponse } from '@core/models';
import { AdminService, SettingsService } from '@core/services';
import { createObjectURL, resizeImage, toIco } from '@core/utils';
import {
  GetSystemTheme,
  ResetUserState,
  SetCompanyLogo,
  SetIsAdmin,
  SetSystemTheme,
  SetTokens,
  SetUser,
  SetUserTheme
} from '@core/actions/user.actions';
import { UserThemeService } from '@core/services/user-theme.service';

/**
 * Models
 */
export namespace User {
  export interface State {
    user: UserLoginResponse;
    theme: Settings.BrandingOutput;
    logo: string;
    icon: string;
    systemTheme: SystemTheme;
    isAdmin: boolean;
  }
}

/**
 * Defaults
 */
export const USER_DEFAULTS: User.State = {
  user: {} as UserLoginResponse,
  theme: {} as Settings.BrandingOutput,
  logo: '',
  icon: '',
  systemTheme: SystemTheme.LIGHT,
  isAdmin: false
};

@State<User.State>({
  name: 'UserState',
  defaults: USER_DEFAULTS
})
@Injectable()
export class UserState {
  constructor(
    private settingsService: SettingsService,
    private userThemeService: UserThemeService,
    private adminService: AdminService
  ) {}

  @Selector()
  static user({ user }: User.State): UserLoginResponse {
    return user;
  }

  @Selector()
  static isInviterUser({ user }: User.State): boolean {
    return !!user?.user.inviter?.inviterId;
  }

  @Selector()
  static isAdmin({ isAdmin }: User.State): boolean {
    return isAdmin;
  }

  @Selector()
  static getUserTheme({ theme }: User.State): Settings.BrandingOutput {
    return theme;
  }

  @Selector()
  static isUserTheme({ theme }: User.State): boolean {
    return !!theme;
  }

  @Selector()
  static getCompanyLogo({ logo }: User.State): string | undefined {
    return logo;
  }

  @Selector()
  static getIcon({ icon }: User.State): string | undefined {
    return icon;
  }

  @Selector()
  static getSystemTheme({ systemTheme }: User.State): string | undefined {
    return systemTheme;
  }

  @Action(SetUser)
  userLogin({ patchState }: StateContext<User.State>, { payload }: SetUser) {
    patchState({ user: payload });
  }

  @Action(SetTokens)
  setToken({ patchState, getState }: StateContext<User.State>, { payload }: SetTokens) {
    const user = getState().user;
    patchState({
      user: {
        ...user,
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken
      }
    });
  }

  @Action(SetUserTheme)
  userTheme({ patchState }: StateContext<User.State>) {
    return this.settingsService.getCustomBranding().pipe(
      tap((result) => {
        patchState({ theme: result.data });
      })
    );
  }

  @Action(SetIsAdmin)
  setIsAdmin({ patchState }: StateContext<User.State>) {
    return this.adminService.isAdmin$().pipe(
      tap((result) => {
        patchState({ isAdmin: result.isAdmin });
      }),
      catchError((error) => {
        patchState({ isAdmin: false });
        return EMPTY;
      })
    );
  }

  @Action(SetSystemTheme)
  systemTheme({ patchState }: StateContext<User.State>, { payload }: SetSystemTheme) {
    return this.userThemeService.setTheme(payload).pipe(
      tap((result) => {
        patchState({ systemTheme: (result.data as unknown as SystemTheme) || SystemTheme.LIGHT });
      })
    );
  }

  @Action(GetSystemTheme)
  getSystemTheme({ patchState }: StateContext<User.State>) {
    return this.userThemeService.getTheme().pipe(
      tap((result) => {
        patchState({ systemTheme: (result.data as unknown as SystemTheme) || SystemTheme.LIGHT });
      })
    );
  }

  @Action(SetCompanyLogo)
  setCompanyLogo({ patchState }: StateContext<User.State>) {
    return this.settingsService.getCompanyLogo().pipe(
      catchError(() => {
        patchState({ logo: '' });
        return of(null);
      }),
      switchMap((blob) => {
        if (blob) {
          return resizeImage(blob, 195, 195).pipe(
            switchMap(toIco),
            map((icon) => ({ logo: createObjectURL(blob), icon: createObjectURL(icon) }))
          );
        }
        return of({ logo: '', icon: '' });
      }),
      tap(patchState)
    );
  }

  @Action(ResetUserState)
  resetState({ patchState }: StateContext<User.State>) {
    patchState({
      user: {} as UserLoginResponse,
      isAdmin: false,
      logo: '',
      icon: ''
    });
  }
}
